/**
 * Consent for third-party measurement. **Off until the visitor says yes**: without it GA4 and
 * the ad pixels load nothing and send nothing (the Intake API's own `enquiry_events` carry no
 * personal data and need no consent).
 *
 * Where it comes from (tasks/decisions.md Q34, INTERIM: Anton 2026-09-29, open for counsel): a
 * first-party banner, the same on the static site (delay-buddy-new `assets/consent.js`) and
 * here (`chrome/consent/`). Both read and write one record, so a choice made on one side holds
 * on the other (same origin):
 *
 *   localStorage.db_consent = {"analytics":bool,"ads":bool,"v":2,"at":"<ISO 8601 UTC>"}
 *
 * - `v` is the banner wording's version. Bump `CONSENT_VERSION` here **and** `VERSION` in
 *   `assets/consent.js` when the wording or the list of tools changes: older choices are asked
 *   again. A newer version than ours counts (one side may deploy before the other).
 * - `at` is when the choice was made; after `CONSENT_MAX_AGE_DAYS` the banner asks again.
 * - A change is announced as a `db:consent` event on `window` (detail `{analytics, ads}`); other
 *   tabs hear it through `storage`. `db:consent-open` (or `openConsentSettings()`) opens the
 *   banner again.
 */
export interface Consent {
  analytics: boolean;
  ads: boolean;
}

/** A choice as stored: the answers, the wording's version and when it was made. */
export interface ConsentChoice extends Consent {
  v: number;
  at: string;
}

export const NO_CONSENT: Consent = { analytics: false, ads: false };
export const CONSENT_KEY = 'db_consent';
export const CONSENT_EVENT = 'db:consent';
export const CONSENT_OPEN_EVENT = 'db:consent-open';
// 2 (w6-02): the "Always on" line names Cloudflare's robot check (Turnstile).
export const CONSENT_VERSION = 2;
/** DECISION-NEEDED (w5-08, counsel): how long a choice lasts before the banner asks again. */
export const CONSENT_MAX_AGE_DAYS = 365;
const DAY_MS = 24 * 60 * 60 * 1000;

function answers(v: unknown): Consent {
  const o = (v ?? {}) as Partial<Record<keyof Consent, unknown>>;
  return { analytics: o.analytics === true, ads: o.ads === true };
}

/** The stored choice, or null when there's none we can use (missing, older wording, expired). */
export function parseChoice(raw: string | null | undefined, now = Date.now()): ConsentChoice | null {
  if (!raw) return null;
  let o: unknown;
  try {
    o = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!o || typeof o !== 'object') return null;
  const { v, at } = o as { v?: unknown; at?: unknown };
  if (typeof v !== 'number' || v < CONSENT_VERSION) return null;
  const when = typeof at === 'string' ? Date.parse(at) : NaN;
  if (Number.isNaN(when) || now - when > CONSENT_MAX_AGE_DAYS * DAY_MS) return null;
  return { ...answers(o), v, at: at as string };
}

/** Only when storage refused the choice (private mode, full): it lasts for this page. */
let memory: string | null = null;

function stored(): string | null {
  try {
    return globalThis.localStorage?.getItem(CONSENT_KEY) ?? memory;
  } catch {
    return memory;
  }
}

/** The visitor's choice, or null when the banner should ask. */
export function readChoice(): ConsentChoice | null {
  return parseChoice(stored());
}

export function readConsent(): Consent {
  const c = readChoice();
  return c ? answers(c) : NO_CONSENT;
}

/* ── Withdrawal ──────────────────────────────────────────── */

const ANALYTICS_COOKIE = /^(_ga|_ga_.+|_gid|_gat.*)$/;
const ADS_COOKIE = /^(_gcl_.+|_fbp|_fbc)$/;

/** The cookies in `cookieString` that a withdrawn category leaves behind. */
export function cookiesToClear(cookieString: string, c: Consent): string[] {
  return cookieString
    .split(';')
    .map((pair) => pair.split('=')[0]!.trim())
    .filter((name) => (!c.analytics && ANALYTICS_COOKIE.test(name)) || (!c.ads && ADS_COOKIE.test(name)));
}

/** Every domain a tag may have set a cookie on: `''` (host-only), the host and its parents. */
export function cookieDomains(hostname: string): string[] {
  const labels = hostname.split('.');
  if (labels.length < 2 || /^[\d.]+$/.test(hostname)) return hostname ? ['', hostname] : [''];
  return ['', hostname, ...labels.slice(0, -1).map((_, i) => `.${labels.slice(i).join('.')}`)];
}

function clearCookies(c: Consent): void {
  const domains = cookieDomains(location.hostname);
  for (const name of cookiesToClear(document.cookie, c))
    for (const d of domains) document.cookie = `${name}=; Max-Age=0; Path=/${d ? `; Domain=${d}` : ''}`;
}

/* ── Writing ─────────────────────────────────────────────── */

/** Records the visitor's choice (with the version and the time) and announces it. */
export function setConsent(c: Consent, now = Date.now()): void {
  const choice: ConsentChoice = { ...answers(c), v: CONSENT_VERSION, at: new Date(now).toISOString() };
  const raw = JSON.stringify(choice);
  try {
    globalThis.localStorage.setItem(CONSENT_KEY, raw);
    memory = null;
  } catch {
    memory = raw; // private mode: consent lasts for this page only
  }
  clearCookies(choice);
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: answers(c) }));
}

/** Opens the banner again (the quiet "Cookie settings" link). */
export function openConsentSettings(): void {
  window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}

/** Calls `listener` with the consent now and on every change. Returns the unsubscribe. */
export function onConsent(listener: (c: Consent) => void): () => void {
  listener(readConsent());
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<unknown>).detail;
    listener(detail ? answers(detail) : readConsent());
  };
  const storage = (e: StorageEvent) => {
    if (e.key === CONSENT_KEY) listener(readConsent());
  };
  window.addEventListener(CONSENT_EVENT, handler);
  window.addEventListener('storage', storage);
  return () => {
    window.removeEventListener(CONSENT_EVENT, handler);
    window.removeEventListener('storage', storage);
  };
}

/** For tests: forget the in-memory fallback. */
export function resetConsentMemory(): void {
  memory = null;
}
